import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DatabaseOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd'

import { adminApi, formatApiError, type PageResult } from '../../api'
import {
  KnowledgeFolderMultiBrowseField,
  formatDateOrDash,
  tablePagination,
  type FolderDimensionOption,
  useServerPagination,
} from '../../components/ui'
import {
  KNOWLEDGE_SCOPE_OPTIONS,
  type KnowledgeScope,
} from '../../constants/knowledgeScope'

type GraphLibraryStatus = {
  enabled: boolean
  available: boolean
  graph_database: { configured: boolean; available: boolean; error?: string }
  neo4j: { configured: boolean; available: boolean; error?: string }
  legacy_available: boolean
}

type GraphLibrary = {
  id: number
  library_key: string
  name: string
  description: string
  scope: KnowledgeScope
  status: string
  is_enabled: boolean
  permission_enabled: boolean
  source_filter?: Partial<SourceFilter>
  chunk_strategy: 'fixed' | 'paragraph' | 'clause' | 'semantic'
  chunk_size: number
  entity_types: string[]
  custom_entities: string[]
  confidence_threshold: number
  synonym_normalization: boolean
  llm_profile_id: number | null
  embedding_profile_id: number | null
  active_build_id: number | null
  document_count: number
  node_count: number
  edge_count: number
  created_by_name: string
  created_at: string
  updated_at: string
}

type ModelProfile = {
  id: number
  profile_name: string
  model_name: string
  provider: string
  model_type: 'llm' | 'embedding'
  is_active: boolean
}

type SourceFilter = {
  folder_ids: number[]
  include_descendants: boolean
  categories: string[]
  tags_any: string[]
  min_quality?: string | null
  doc_status?: string | null
  years: number[]
  created_by: string
  source: string
  q: string
}

type SourceConfig = {
  revision: string
  filter: SourceFilter
  include_item_ids: number[]
  exclude_item_ids: number[]
}

type SourceForm = SourceFilter & {
  include_item_ids: string[]
  exclude_item_ids: string[]
}

type GraphDocument = {
  item_id: number
  title: string
  category: string
  source: string
  status: string
  chunk_count: number
  entity_count: number
  relation_count: number
  review_count: number
  summary: string
  character_count: number
  duration_ms?: number | null
  started_at?: string | null
  finished_at?: string | null
  error_message?: string | null
  updated_at: string
}

type ExtractionRow = {
  target_key: string
  title: string
  description: string
  confidence: number
  status: string
  aliases?: string[]
  properties?: Record<string, unknown>
  directed?: boolean
  strength?: string
  evidence_quote: string
  source_locatable: boolean
}

type GraphBuild = {
  id: number
  build_key: string
  build_type: string
  status: string
  progress: number
  total: number
  succeeded: number
  review_count: number
  rejected_count: number
  failed: number
  status_message: string
  error_message?: string | null
  requested_item_id?: number | null
  created_at: string
  started_at?: string | null
  finished_at?: string | null
}

type ReviewRow = {
  target_type: 'entity' | 'relation'
  target_key: string
  title: string
  description: string
  confidence: number
  status: string
  build_key: string
  library_key?: string
  evidence_quote: string
  item_id?: number | null
}

type AclConfig = {
  permission_enabled: boolean
  entries: { identity_provider: 'local'; subject_type: 'user' | 'role'; subject_key: string }[]
}

type LocalUser = {
  id: number
  username: string
  display_name: string
  is_active: boolean
}

type LocalRole = { id: number; name: string; code: string }

type KnowledgeDocumentOption = {
  id: number
  title: string
  category?: string
  created_by_name?: string
  created_at?: string
}

const BUILTIN_ENTITY_TYPES = ['组织/人员', '原文内容', '业务要素', '主题词']
const DEFAULT_ENTITY_TYPES = ['组织/人员', '业务要素']
const RESERVED_ENTITY_TYPES = new Set([...BUILTIN_ENTITY_TYPES,
  '文档', '档案', '档案来源', '来源', '切片', '知识库', '图谱库',
  '知识文档', '知识条目', 'knowledgedocument', 'knowledgecategory', 'category',
  'document', 'archive', 'source', 'chunk', 'folder',
])

function normalizedRuleNames(values: string[] | undefined) {
  return Array.from(new Set((values || []).map(value => value.normalize('NFKC').trim()).filter(Boolean)))
}

function extractionRuleError(entityTypes: string[] = [], customEntities: string[] = []) {
  if (entityTypes.length > 50 || customEntities.length > 50) return '每组规则最多配置 50 项'
  if ([...entityTypes, ...customEntities].some(type => /[\p{Cc}\p{Cf}\p{Cs}]/u.test(type))) return '类型名称不能包含控制字符或不可见字符'
  if ([...entityTypes, ...customEntities].some(type => !type.normalize('NFKC').trim())) return '类型名称不能为空'
  const builtin = normalizedRuleNames(entityTypes)
  const custom = normalizedRuleNames(customEntities)
  if (builtin.some(type => !BUILTIN_ENTITY_TYPES.includes(type))) return '内置类型不受支持，请重新选择'
  if (custom.some(type => Array.from(type).length > 32)) return '自定义类型名称最多 32 个字符'
  if (custom.some(type => RESERVED_ENTITY_TYPES.has(type.toLowerCase()))) return '自定义名称不能与内置类型或来源结构类型重名'
  if (!builtin.length && !custom.length) return '请至少配置一种实体类型，可以只选择自定义'
  return ''
}

function extractionRuleKey(values: { entity_types?: string[]; custom_entities?: string[] }) {
  return JSON.stringify([normalizedRuleNames(values.entity_types).sort(), normalizedRuleNames(values.custom_entities).sort()])
}
const ACTIVE_BUILD_STATUSES = new Set(['queued', 'running', 'syncing', 'waiting_projection'])

const LIBRARY_STATUS: Record<string, { label: string; color?: string }> = {
  draft: { label: '草稿' },
  building: { label: '构建中', color: 'processing' },
  syncing: { label: '同步中', color: 'processing' },
  ready: { label: '可用', color: 'success' },
  ready_with_warnings: { label: '可用，有警告', color: 'warning' },
  stale: { label: '待重建', color: 'warning' },
  failed: { label: '失败', color: 'error' },
}

const BUILD_STATUS: Record<string, { label: string; color?: string }> = {
  queued: { label: '排队中', color: 'processing' },
  running: { label: '抽取中', color: 'processing' },
  syncing: { label: '同步 Neo4j', color: 'processing' },
  waiting_projection: { label: '等待同步', color: 'warning' },
  completed: { label: '完成', color: 'success' },
  partial: { label: '部分完成', color: 'warning' },
  failed: { label: '失败', color: 'error' },
}

const DOCUMENT_STATUS: Record<string, { label: string; color?: string }> = {
  pending: { label: '待构建' },
  running: { label: '抽取中', color: 'processing' },
  completed: { label: '已完成', color: 'success' },
  review: { label: '待复核', color: 'warning' },
  rejected: { label: '已拒绝' },
  failed: { label: '失败', color: 'error' },
}

const DOCUMENT_STATUS_OPTIONS = Object.entries(DOCUMENT_STATUS).map(([value, meta]) => ({
  value,
  label: meta.label,
}))

function StatusTag({ value, build = false }: { value: string; build?: boolean }) {
  const meta = (build ? BUILD_STATUS : LIBRARY_STATUS)[value] || { label: value || '未知' }
  return <Tag color={meta.color}>{meta.label}</Tag>
}

function DocumentStatusTag({ value }: { value: string }) {
  const meta = DOCUMENT_STATUS[value] || { label: value || '未知' }
  return <Tag color={meta.color}>{meta.label}</Tag>
}

function positiveIds(values: unknown): number[] {
  const rows = Array.isArray(values) ? values : []
  return [...new Set(rows.map(Number).filter((value) => Number.isInteger(value) && value > 0))]
}

const CHUNK_STRATEGY_LABELS: Record<GraphLibrary['chunk_strategy'], string> = {
  fixed: '固定长度',
  paragraph: '段落',
  clause: '条款',
  semantic: '语义',
}

function sourceSummary(library: GraphLibrary): string {
  const source = library.source_filter || {}
  const folders = Array.isArray(source.folder_ids) ? source.folder_ids.length : 0
  const categories = Array.isArray(source.categories) ? source.categories.length : 0
  const parts = []
  if (folders) parts.push(`${folders} 个目录`)
  if (categories) parts.push(`${categories} 个分类`)
  if (source.q) parts.push(`标题“${source.q}”`)
  if (source.source) parts.push(`来源“${source.source}”`)
  return parts.join(' · ') || '当前知识范围'
}

function DocumentMultiPicker({
  scope,
  value = [],
  onChange,
  placeholder,
}: {
  scope: KnowledgeScope
  value?: string[]
  onChange?: (value: string[]) => void
  placeholder: string
}) {
  const [documents, setDocuments] = useState<KnowledgeDocumentOption[]>([])
  const [loading, setLoading] = useState(false)
  const requestRef = useRef(0)
  const timerRef = useRef(0)

  const load = useCallback((q = '') => {
    const requestId = ++requestRef.current
    setLoading(true)
    adminApi
      .get<PageResult<KnowledgeDocumentOption>>('/admin/knowledge/items', {
        params: { scope, q: q.trim(), page: 1, page_size: 20 },
      })
      .then((response) => {
        if (requestId === requestRef.current) setDocuments(response.data.items || [])
      })
      .catch((error) => {
        if (requestId === requestRef.current) message.error(formatApiError(error, '文档搜索失败'))
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false)
      })
  }, [scope])

  useEffect(() => {
    load()
    return () => window.clearTimeout(timerRef.current)
  }, [load])

  const options = useMemo(() => {
    const rows = documents.map((item) => ({
      value: String(item.id),
      label: `${item.title} (#${item.id})${item.category ? ` · ${item.category}` : ''}${item.created_by_name ? ` · ${item.created_by_name}` : ''}`,
    }))
    const known = new Set(rows.map((item) => item.value))
    return [...rows, ...value.filter((id) => !known.has(id)).map((id) => ({ value: id, label: `文档 #${id}` }))]
  }, [documents, value])

  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      filterOption={false}
      loading={loading}
      value={value}
      options={options}
      placeholder={placeholder}
      maxTagCount="responsive"
      onFocus={() => load()}
      onSearch={(query) => {
        window.clearTimeout(timerRef.current)
        timerRef.current = window.setTimeout(() => load(query), 250)
      }}
      onChange={onChange}
    />
  )
}

function confirmBuild(content: string, run: () => Promise<void>) {
  Modal.confirm({
    title: '确认调用模型构建图谱？',
    content,
    okText: '确认构建',
    cancelText: '取消',
    onOk: run,
  })
}

function formatDuration(value?: number | null): string {
  if (value == null) return '-'
  if (value < 1000) return `${value} ms`
  if (value < 60_000) return `${(value / 1000).toFixed(1)} 秒`
  return `${(value / 60_000).toFixed(1)} 分钟`
}

function LibraryEditor({
  open,
  library,
  llmProfiles,
  embeddingProfiles,
  onClose,
  onSaved,
}: {
  open: boolean
  library: GraphLibrary | null
  llmProfiles: ModelProfile[]
  embeddingProfiles: ModelProfile[]
  onClose: () => void
  onSaved: () => void
}) {
  const [form] = Form.useForm<GraphLibrary>()
  const [saving, setSaving] = useState(false)
  const strategy = Form.useWatch('chunk_strategy', form)
  const entityTypes = Form.useWatch('entity_types', form)
  const unsupportedTypes = normalizedRuleNames(entityTypes).filter(type => !BUILTIN_ENTITY_TYPES.includes(type))

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      library || {
        name: '',
        description: '',
        scope: 'enterprise',
        permission_enabled: true,
        chunk_strategy: 'paragraph',
        chunk_size: 1200,
        entity_types: DEFAULT_ENTITY_TYPES,
        custom_entities: [],
        confidence_threshold: 0.75,
        synonym_normalization: true,
        llm_profile_id: null,
        embedding_profile_id: null,
      },
    )
  }, [form, library, open])

  const save = async () => {
    let values: GraphLibrary
    try { values = await form.validateFields() }
    catch { return }
    if (values.chunk_strategy === 'semantic' && !values.embedding_profile_id) {
      message.warning('语义分块必须选择 Embedding 模型')
      return
    }
    setSaving(true)
    try {
      const payload = {
        name: values.name.trim(),
        description: (values.description || '').trim(),
        scope: values.scope,
        permission_enabled: values.permission_enabled,
        chunk_strategy: values.chunk_strategy,
        chunk_size: values.chunk_size,
        entity_types: BUILTIN_ENTITY_TYPES.filter(type => normalizedRuleNames(values.entity_types).includes(type)),
        custom_entities: normalizedRuleNames(values.custom_entities),
        confidence_threshold: values.confidence_threshold,
        synonym_normalization: values.synonym_normalization,
        llm_profile_id: values.llm_profile_id || null,
        embedding_profile_id: values.embedding_profile_id || null,
      }
      if (library) await adminApi.patch(`/admin/graph-libraries/${library.library_key}`, payload)
      else await adminApi.post('/admin/graph-libraries', payload)
      message.success(library && extractionRuleKey(library) !== extractionRuleKey(payload)
        ? '抽取规则已更新，下次重新构建生效' : library ? '图谱库配置已保存' : '图谱库已创建')
      onSaved()
    } catch (error) {
      message.error(formatApiError(error, '图谱库保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const profileOptions = (items: ModelProfile[]) =>
    items.filter((item) => item.is_active).map((item) => ({
      value: item.id,
      label: `${item.profile_name}${item.model_name ? ` (${item.model_name})` : ''}`,
    }))

  return (
    <Modal
      width="min(92vw, 760px)"
      title={library ? '编辑图谱库' : '新建图谱库'}
      open={open}
      confirmLoading={saving}
      okText="保存"
      cancelText="取消"
      onOk={() => void save()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" className="graph-library-form">
        <div className="graph-library-form-grid">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item name="scope" label="知识范围" rules={[{ required: true }]}>
            <Select options={KNOWLEDGE_SCOPE_OPTIONS} disabled={Boolean(library?.active_build_id)} />
          </Form.Item>
        </div>
        <Form.Item name="description" label="说明">
          <Input.TextArea maxLength={512} autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>
        <div className="graph-library-form-grid">
          <Form.Item name="chunk_strategy" label="分块策略" rules={[{ required: true }]}>
            <Select options={[
              { value: 'fixed', label: '固定长度' },
              { value: 'paragraph', label: '段落' },
              { value: 'clause', label: '条款' },
              { value: 'semantic', label: '语义' },
            ]} />
          </Form.Item>
          <Form.Item name="chunk_size" label="分块长度" rules={[{ required: true }]}>
            <InputNumber min={200} max={8000} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="llm_profile_id" label="抽取模型" extra="未指定时使用 graph_extraction 智能体绑定模型">
            <Select allowClear options={profileOptions(llmProfiles)} placeholder="使用智能体默认模型" />
          </Form.Item>
          <Form.Item
            name="embedding_profile_id"
            label="Embedding 模型"
            rules={strategy === 'semantic' ? [{ required: true, message: '语义分块必须选择模型' }] : []}
          >
            <Select allowClear disabled={strategy !== 'semantic'} options={profileOptions(embeddingProfiles)} />
          </Form.Item>
          <Form.Item name="confidence_threshold" label="自动确认阈值" rules={[{ required: true }]}>
            <InputNumber min={0.6} max={1} step={0.05} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="permission_enabled" label="开启图谱库权限" valuePropName="checked">
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
        </div>
        <Form.Item name="entity_types" label="抽取实体定义规则" dependencies={['custom_entities']} rules={[{
          validator: () => {
            const error = extractionRuleError(form.getFieldValue('entity_types'), form.getFieldValue('custom_entities'))
            return error ? Promise.reject(new Error(error)) : Promise.resolve()
          },
        }]} extra={unsupportedTypes.length
          ? `当前包含历史不支持的类型：${unsupportedTypes.join('、')}。请点击标签 × 移除，也可以改为自定义类型；取消编辑会保留原配置。`
          : '允许仅配置自定义类型；保存后在下一次重新构建时生效。'}>
          <Select mode="multiple" allowClear options={BUILTIN_ENTITY_TYPES
            .filter(type => type !== '原文内容' || library?.entity_types?.includes(type))
            .map(type => ({ value: type, label: type }))} />
        </Form.Item>
        <Form.Item name="custom_entities" label="自定义实体类型" dependencies={['entity_types']} rules={[{
          validator: () => {
            const error = extractionRuleError(form.getFieldValue('entity_types'), form.getFieldValue('custom_entities'))
            return error ? Promise.reject(new Error(error)) : Promise.resolve()
          },
        }]} extra="填写类型名称，例如合同金额、验收条款，不是要抽取的具体值。">
          <Select mode="tags" tokenSeparators={[',', '，']} maxCount={50} placeholder="如：合同金额、验收条款" />
        </Form.Item>
        <Form.Item name="synonym_normalization" label="按同义词词典自动归一" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}

function SourcesPane({ library, onChanged }: { library: GraphLibrary; onChanged: () => void }) {
  const [form] = Form.useForm<SourceForm>()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [revision, setRevision] = useState('')
  const [conflicted, setConflicted] = useState(false)
  const [dimensions, setDimensions] = useState<FolderDimensionOption[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [tags, setTags] = useState<string[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [sourceResponse, dimensionResponse, categoryResponse, tagResponse] = await Promise.all([
        adminApi.get<SourceConfig>(`/admin/graph-libraries/${library.library_key}/sources`),
        adminApi.get<{ items: FolderDimensionOption[] }>('/admin/knowledge/folder-dimensions', { params: { scope: library.scope } }),
        adminApi.get<{ items: { name: string }[] }>('/admin/knowledge/doc-categories', { params: { active_only: true } }),
        adminApi.get<{ items: string[] }>('/admin/knowledge/tags', { params: { scope: library.scope } }),
      ])
      const source = sourceResponse.data
      setRevision(source.revision)
      setConflicted(false)
      form.setFieldsValue({
        ...source.filter,
        folder_ids: source.filter.folder_ids || [],
        categories: source.filter.categories || [],
        tags_any: source.filter.tags_any || [],
        years: source.filter.years || [],
        include_descendants: source.filter.include_descendants !== false,
        include_item_ids: (source.include_item_ids || []).map(String),
        exclude_item_ids: (source.exclude_item_ids || []).map(String),
      })
      setDimensions(dimensionResponse.data.items || [])
      setCategories((categoryResponse.data.items || []).map((item) => item.name).filter(Boolean))
      setTags(tagResponse.data.items || [])
    } catch (error) {
      message.error(formatApiError(error, '来源配置加载失败'))
    } finally {
      setLoading(false)
    }
  }, [form, library.library_key, library.scope])

  useEffect(() => {
    void load()
  }, [load])

  const save = async () => {
    const values = await form.validateFields()
    if (!revision) {
      message.error('来源配置尚未加载完成')
      return
    }
    setSaving(true)
    try {
      await adminApi.put(`/admin/graph-libraries/${library.library_key}/sources`, {
        expected_revision: revision,
        filter: {
          folder_ids: values.folder_ids || [],
          include_descendants: values.include_descendants !== false,
          categories: values.categories || [],
          tags_any: values.tags_any || [],
          min_quality: values.min_quality || null,
          doc_status: values.doc_status || null,
          years: positiveIds(values.years),
          created_by: (values.created_by || '').trim(),
          source: (values.source || '').trim(),
          q: (values.q || '').trim(),
        },
        include_item_ids: positiveIds(values.include_item_ids),
        exclude_item_ids: positiveIds(values.exclude_item_ids),
      })
      await load()
      message.success('图谱来源已保存')
      onChanged()
    } catch (error) {
      if ((error as { httpStatus?: number }).httpStatus === 409) {
        setConflicted(true)
      }
      message.error(formatApiError(error, '来源配置保存失败'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Spin spinning={loading}>
      <Form form={form} layout="vertical" className="graph-library-pane-form">
        {conflicted ? (
          <Alert
            type="warning"
            showIcon
            title="来源已更新"
            description="后台同步或其他管理员已修改来源。当前输入已保留，请重新加载后再确认。"
            action={<Button icon={<ReloadOutlined />} onClick={() => void load()}>重新加载来源</Button>}
          />
        ) : null}
        <Alert type="info" showIcon title={`当前来源限定在${library.scope === 'enterprise' ? '企业' : '个人'}知识库`} />
        <Form.Item name="folder_ids" label="知识目录">
          <KnowledgeFolderMultiBrowseField
            block
            variant="input"
            scope={library.scope}
            dimensions={dimensions}
            placeholder="不限目录"
          />
        </Form.Item>
        <Form.Item name="include_descendants" label="包含子目录" valuePropName="checked">
          <Switch />
        </Form.Item>
        <div className="graph-library-form-grid">
          <Form.Item name="categories" label="分类">
            <Select mode="tags" allowClear options={categories.map((value) => ({ value }))} />
          </Form.Item>
          <Form.Item name="tags_any" label="标签（命中任一）">
            <Select mode="tags" allowClear options={tags.map((value) => ({ value }))} />
          </Form.Item>
          <Form.Item name="min_quality" label="最低质量">
            <Select allowClear options={[
              { value: 'unknown', label: '未评及以上' },
              { value: 'low', label: '低质量及以上' },
              { value: 'medium', label: '中质量及以上' },
              { value: 'high', label: '高质量' },
            ]} />
          </Form.Item>
          <Form.Item name="doc_status" label="文档状态">
            <Select allowClear options={[{ value: 'normal', label: '正式' }, { value: 'draft', label: '草稿' }]} />
          </Form.Item>
          <Form.Item name="years" label="创建年份">
            <Select mode="tags" tokenSeparators={[',', '，']} />
          </Form.Item>
          <Form.Item name="created_by" label="创建人包含">
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item name="source" label="来源包含">
            <Input maxLength={256} />
          </Form.Item>
          <Form.Item name="q" label="标题包含">
            <Input maxLength={256} />
          </Form.Item>
        </div>
        <div className="graph-library-form-grid">
          <Form.Item name="include_item_ids" label="手动纳入文档">
            <DocumentMultiPicker scope={library.scope} placeholder="按标题搜索并选择需要额外纳入的文档" />
          </Form.Item>
          <Form.Item name="exclude_item_ids" label="手动排除文档">
            <DocumentMultiPicker scope={library.scope} placeholder="按标题搜索并选择需要排除的文档" />
          </Form.Item>
        </div>
        <div className="graph-library-pane-actions">
          <Button icon={<ReloadOutlined />} onClick={() => void load()}>重载</Button>
          <Button type="primary" loading={saving} onClick={() => void save()}>保存来源</Button>
        </div>
      </Form>
    </Spin>
  )
}

function DocumentExtractionDrawer({
  library,
  document,
  onClose,
}: {
  library: GraphLibrary
  document: GraphDocument | null
  onClose: () => void
}) {
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [targetType, setTargetType] = useState<'entity' | 'relation'>('entity')
  const [rows, setRows] = useState<ExtractionRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!document) return
    setLoading(true)
    adminApi
      .get<PageResult<ExtractionRow>>(
        `/admin/graph-libraries/${library.library_key}/documents/${document.item_id}/extractions`,
        { params: { target_type: targetType, page, page_size: pageSize } },
      )
      .then((response) => {
        setRows(response.data.items || [])
        setTotal(response.data.total || 0)
      })
      .catch((error) => message.error(formatApiError(error, '抽取明细加载失败')))
      .finally(() => setLoading(false))
  }, [document, library.library_key, page, pageSize, targetType])

  return (
    <Drawer
      size="min(96vw, 920px)"
      title={document ? `${document.title} · 抽取明细` : '抽取明细'}
      open={Boolean(document)}
      onClose={onClose}
      destroyOnHidden
    >
      <div className="graph-library-pane-toolbar">
        <Segmented
          value={targetType}
          options={[{ value: 'entity', label: '实体' }, { value: 'relation', label: '关系' }]}
          onChange={(value) => { resetPage(); setTargetType(value as 'entity' | 'relation') }}
        />
        {document ? <Typography.Text type="secondary">摘要：{document.summary || '暂无摘要'}</Typography.Text> : null}
      </div>
      <Table<ExtractionRow>
        rowKey="target_key"
        size="small"
        loading={loading}
        dataSource={rows}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        scroll={{ x: 900 }}
        columns={[
          { title: targetType === 'entity' ? '实体' : '关系', dataIndex: 'title', width: 240, ellipsis: true },
          { title: '类型', dataIndex: 'description', width: 110, ellipsis: true },
          { title: '置信度', dataIndex: 'confidence', width: 84, render: (value: number) => `${(Number(value || 0) * 100).toFixed(0)}%` },
          { title: '状态', dataIndex: 'status', width: 90, render: (value: string) => <Tag>{value}</Tag> },
          { title: '证据原句', dataIndex: 'evidence_quote', ellipsis: true },
          { title: '可定位', dataIndex: 'source_locatable', width: 76, render: (value: boolean) => <Tag color={value ? 'success' : 'default'}>{value ? '是' : '否'}</Tag> },
        ]}
      />
    </Drawer>
  )
}

function DocumentsPane({ library }: { library: GraphLibrary }) {
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [rows, setRows] = useState<GraphDocument[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [appliedQuery, setAppliedQuery] = useState('')
  const [status, setStatus] = useState<string | undefined>()
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({})
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([])
  const [detailDocument, setDetailDocument] = useState<GraphDocument | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await adminApi.get<PageResult<GraphDocument> & { status_counts?: Record<string, number> }>(
        `/admin/graph-libraries/${library.library_key}/documents`,
        { params: { page, page_size: pageSize, q: appliedQuery, status } },
      )
      setRows(response.data.items)
      setTotal(response.data.total)
      setStatusCounts(response.data.status_counts || {})
    } catch (error) {
      message.error(formatApiError(error, '来源文档加载失败'))
    } finally {
      setLoading(false)
    }
  }, [appliedQuery, library.library_key, page, pageSize, status])

  useEffect(() => {
    void load()
  }, [load])

  const retryDocument = (itemId: number) => {
    confirmBuild(`仅重新抽取文档 ${itemId}，会产生新的构建版本。`, async () => {
      await adminApi.post(`/admin/graph-libraries/${library.library_key}/documents/${itemId}/retry`, { confirmed: true })
      message.success('单文档重试已排队')
      await load()
    })
  }

  const retrySelected = () => {
    if (!selectedItemIds.length) return
    confirmBuild(`重新抽取选中的 ${selectedItemIds.length} 篇文档，其余文档继续沿用当前生效版本。`, async () => {
      await adminApi.post(`/admin/graph-libraries/${library.library_key}/documents/retry-batch`, {
        confirmed: true,
        item_ids: selectedItemIds,
      })
      message.success('批量重试已排队')
      setSelectedItemIds([])
      await load()
    })
  }

  return (
    <>
      <div className="graph-library-pane-toolbar">
        <Input.Search
          allowClear
          value={query}
          placeholder="搜索文档标题"
          onChange={(event) => setQuery(event.target.value)}
          onSearch={() => { resetPage(); setAppliedQuery(query.trim()) }}
        />
        <Select
          allowClear
          placeholder="执行状态"
          value={status}
          options={DOCUMENT_STATUS_OPTIONS}
          onChange={(value) => { resetPage(); setStatus(value) }}
        />
        <Button disabled={!selectedItemIds.length || !library.active_build_id} onClick={retrySelected}>批量重试{selectedItemIds.length ? ` ${selectedItemIds.length}` : ''}</Button>
      </div>
      <div className="graph-library-status-summary">
        {Object.entries(statusCounts).map(([key, count]) => (
          <button type="button" key={key} className={status === key ? 'is-active' : ''} onClick={() => { resetPage(); setStatus(status === key ? undefined : key) }}>
            <span>{DOCUMENT_STATUS[key]?.label || key}</span><strong>{count}</strong>
          </button>
        ))}
      </div>
      <Table<GraphDocument>
        rowKey="item_id"
        size="small"
        loading={loading}
        dataSource={rows}
        rowSelection={{
          selectedRowKeys: selectedItemIds,
          onChange: (keys) => setSelectedItemIds(keys.map(Number)),
        }}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        scroll={{ x: 1320 }}
        columns={[
          { title: 'ID', dataIndex: 'item_id', width: 72 },
          { title: '文档标题', dataIndex: 'title', ellipsis: true },
          { title: '知识摘要', dataIndex: 'summary', width: 220, ellipsis: true, render: (value: string) => value || '-' },
          { title: '状态', dataIndex: 'status', width: 100, render: (value: string) => <DocumentStatusTag value={value} /> },
          { title: '切片', dataIndex: 'chunk_count', width: 70 },
          { title: '实体', dataIndex: 'entity_count', width: 70 },
          { title: '关系', dataIndex: 'relation_count', width: 70 },
          { title: '待复核', dataIndex: 'review_count', width: 76 },
          { title: '字符数', dataIndex: 'character_count', width: 90, render: (value: number) => value ? value.toLocaleString() : '-' },
          { title: '抽取耗时', dataIndex: 'duration_ms', width: 100, render: formatDuration },
          { title: '更新时间', dataIndex: 'updated_at', width: 160, render: formatDateOrDash },
          {
            title: '操作',
            width: 128,
            fixed: 'right',
            render: (_value, row) => (
              <Space size={0}>
                <Button type="link" size="small" onClick={() => setDetailDocument(row)}>查看</Button>
                <Button type="link" size="small" disabled={!library.active_build_id} onClick={() => retryDocument(row.item_id)}>重试</Button>
              </Space>
            ),
          },
        ]}
      />
      <DocumentExtractionDrawer library={library} document={detailDocument} onClose={() => setDetailDocument(null)} />
    </>
  )
}

function BuildsPane({ library, onChanged }: { library: GraphLibrary; onChanged: () => void }) {
  const { page, pageSize, handlePaginationChange, paginationOptions } = useServerPagination()
  const [rows, setRows] = useState<GraphBuild[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const response = await adminApi.get<PageResult<GraphBuild>>(
        `/admin/graph-libraries/${library.library_key}/builds`,
        { params: { page, page_size: pageSize } },
      )
      setRows(response.data.items)
      setTotal(response.data.total)
      if (silent && !response.data.items.some((row) => ACTIVE_BUILD_STATUSES.has(row.status))) {
        onChanged()
      }
    } catch (error) {
      if (!silent) message.error(formatApiError(error, '构建任务加载失败'))
    } finally {
      if (!silent) setLoading(false)
    }
  }, [library.library_key, onChanged, page, pageSize])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!rows.some((row) => ACTIVE_BUILD_STATUSES.has(row.status))) return
    const timer = window.setInterval(() => void load(true), 2000)
    return () => window.clearInterval(timer)
  }, [load, rows])

  const startBuild = () => {
    confirmBuild('系统会按当前来源读取原文并调用 LLM，完成校验后才切换生效版本。', async () => {
      await adminApi.post(`/admin/graph-libraries/${library.library_key}/builds`, { confirmed: true })
      message.success('构建任务已排队')
      await load()
      onChanged()
    })
  }

  const retryBuild = (row: GraphBuild) => {
    confirmBuild(`重试构建 ${row.build_key}。`, async () => {
      await adminApi.post(`/admin/graph-libraries/${library.library_key}/builds/${row.build_key}/retry`, { confirmed: true })
      message.success('重试任务已排队')
      await load()
    })
  }

  const active = rows.some((row) => ACTIVE_BUILD_STATUSES.has(row.status))
  return (
    <>
      <div className="graph-library-pane-toolbar graph-library-pane-toolbar--end">
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>刷新</Button>
        <Button type="primary" disabled={active || !library.is_enabled} onClick={startBuild}>
          {library.active_build_id ? '重新构建' : '开始首次构建'}
        </Button>
      </div>
      <Table<GraphBuild>
        rowKey="build_key"
        size="small"
        loading={loading}
        dataSource={rows}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        scroll={{ x: 980 }}
        columns={[
          { title: '任务', dataIndex: 'build_key', width: 170, ellipsis: true },
          { title: '类型', dataIndex: 'build_type', width: 110 },
          { title: '状态', dataIndex: 'status', width: 120, render: (value: string) => <StatusTag value={value} build /> },
          {
            title: '进度',
            width: 180,
            render: (_value, row) => <Progress percent={Math.round(Number(row.progress || 0))} size="small" status={row.status === 'failed' ? 'exception' : undefined} />,
          },
          { title: '成功/总数', width: 100, render: (_value, row) => `${row.succeeded}/${row.total}` },
          { title: '待复核', dataIndex: 'review_count', width: 76 },
          { title: '失败', dataIndex: 'failed', width: 68 },
          { title: '状态说明', dataIndex: 'status_message', ellipsis: true },
          { title: '创建时间', dataIndex: 'created_at', width: 160, render: formatDateOrDash },
          {
            title: '操作',
            width: 84,
            fixed: 'right',
            render: (_value, row) => (
              <Button type="link" size="small" disabled={ACTIVE_BUILD_STATUSES.has(row.status)} onClick={() => retryBuild(row)}>
                重试
              </Button>
            ),
          },
        ]}
      />
    </>
  )
}

function ReviewsPane({ library }: { library: GraphLibrary }) {
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [rows, setRows] = useState<ReviewRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [targetType, setTargetType] = useState<'entity' | 'relation'>('entity')
  const [status, setStatus] = useState<'review' | 'confirmed' | 'rejected'>('review')
  const [actionLocks, setActionLocks] = useState<Record<string, boolean>>({})
  const [failedAttempt, setFailedAttempt] = useState<{
    row: ReviewRow
    nextStatus: 'confirmed' | 'rejected'
    requestKey: string
    contextKey: string
    libraryKey: string
  } | null>(null)
  const loadSequenceRef = useRef(0)
  const actionLocksRef = useRef(new Set<string>())
  const contextKey = `${library.library_key}:${targetType}:${status}:${page}:${pageSize}`
  const contextKeyRef = useRef(contextKey)

  const load = useCallback(async () => {
    const sequence = ++loadSequenceRef.current
    const requestedLibraryKey = library.library_key
    setLoading(true)
    try {
      const response = await adminApi.get<PageResult<ReviewRow>>(
        `/admin/graph-libraries/${library.library_key}/reviews`,
        { params: { target_type: targetType, status, page, page_size: pageSize } },
      )
      if (loadSequenceRef.current !== sequence) return
      contextKeyRef.current = contextKey
      setFailedAttempt(null)
      setRows(response.data.items.map((row) => ({
        ...row,
        library_key: requestedLibraryKey,
      })))
      setTotal(response.data.total)
    } catch (error) {
      if (loadSequenceRef.current === sequence) {
        message.error(formatApiError(error, '审核列表加载失败'))
      }
    } finally {
      if (loadSequenceRef.current === sequence) setLoading(false)
    }
  }, [contextKey, library.library_key, page, pageSize, status, targetType])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    contextKeyRef.current = contextKey
  }, [contextKey])

  const submitReview = async (attempt: NonNullable<typeof failedAttempt>) => {
    const rowKey = `${attempt.libraryKey}:${attempt.row.build_key}:${attempt.row.target_type}:${attempt.row.target_key}`
    if (actionLocksRef.current.has(rowKey)) return
    actionLocksRef.current.add(rowKey)
    setActionLocks((current) => ({ ...current, [rowKey]: true }))
    try {
      const response = await adminApi.patch<{
        projection_status?: string
        projection_message?: string
      }>(
        `/admin/graph-libraries/${attempt.libraryKey}/reviews/${attempt.row.target_type}/${attempt.row.target_key}`,
        {
          status: attempt.nextStatus,
          expected_status: attempt.row.status,
          expected_build_key: attempt.row.build_key,
          idempotency_key: attempt.requestKey,
        },
      )
      if (contextKeyRef.current !== attempt.contextKey) return
      setFailedAttempt(null)
      const projectionStatus = response.data.projection_status || 'pending'
      const projectionMessage = response.data.projection_message
        || (attempt.nextStatus === 'confirmed' ? '审核已保存，正在同步正式图谱' : '拒绝已保存，正在同步正式图谱')
      if (projectionStatus === 'failed') message.error(projectionMessage)
      else if (projectionStatus === 'retry') message.warning(projectionMessage)
      else message.success(projectionMessage)
      if (rows.length === 1 && page > 1) handlePaginationChange(page - 1, pageSize)
      else await load()
    } catch (error) {
      if (contextKeyRef.current !== attempt.contextKey) return
      setFailedAttempt(attempt)
      message.error(formatApiError(error, '审核操作失败，请重试或刷新列表'))
    } finally {
      actionLocksRef.current.delete(rowKey)
      setActionLocks((current) => {
        const next = { ...current }
        delete next[rowKey]
        return next
      })
    }
  }

  const review = (row: ReviewRow, nextStatus: 'confirmed' | 'rejected') => {
    const rowLibraryKey = row.library_key || library.library_key
    const attempt = {
      row,
      nextStatus,
      requestKey: crypto.randomUUID(),
      contextKey,
      libraryKey: rowLibraryKey,
    }
    Modal.confirm({
      title: nextStatus === 'confirmed' ? '确认审核对象？' : '拒绝审核对象？',
      content: `“${row.title}”将${nextStatus === 'confirmed' ? '进入正式图谱' : '从正式图谱及关联边中移除'}。`,
      okText: nextStatus === 'confirmed' ? '确认' : '拒绝',
      okButtonProps: nextStatus === 'rejected' ? { danger: true } : undefined,
      cancelText: '取消',
      onOk: () => submitReview(attempt),
    })
  }

  return (
    <>
      <div className="graph-library-pane-toolbar">
        <Segmented
          value={targetType}
          options={[{ value: 'entity', label: '实体' }, { value: 'relation', label: '关系' }]}
          onChange={(value) => { resetPage(); setTargetType(value as 'entity' | 'relation') }}
        />
        <Select
          value={status}
          options={[{ value: 'review', label: '待复核' }, { value: 'confirmed', label: '已确认' }, { value: 'rejected', label: '已拒绝' }]}
          onChange={(value) => { resetPage(); setStatus(value) }}
        />
      </div>
      {failedAttempt && failedAttempt.contextKey === contextKey ? (
        <Alert
          type="error"
          showIcon
          message="上次审核请求未确认完成"
          description="可复用同一请求标识重试，或刷新列表核对实际状态。"
          action={(
            <Space>
              <Button size="small" onClick={() => void load()}>刷新列表</Button>
              <Button size="small" type="primary" onClick={() => void submitReview(failedAttempt)}>重试提交</Button>
            </Space>
          )}
        />
      ) : null}
      <Table<ReviewRow>
        rowKey={(row) => `${row.library_key || library.library_key}:${row.build_key}:${row.target_type}:${row.target_key}`}
        size="small"
        loading={loading}
        dataSource={rows}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        scroll={{ x: 820 }}
        columns={[
          { title: targetType === 'entity' ? '实体' : '关系', dataIndex: 'title', ellipsis: true },
          { title: '类型', dataIndex: 'description', width: 120, ellipsis: true },
          { title: '置信度', dataIndex: 'confidence', width: 90, render: (value: number) => `${(Number(value || 0) * 100).toFixed(0)}%` },
          { title: '证据原句', dataIndex: 'evidence_quote', ellipsis: true },
          { title: '文档 ID', dataIndex: 'item_id', width: 90 },
          {
            title: '操作',
            width: 138,
            fixed: 'right',
            render: (_value, row) => {
              const rowKey = `${row.library_key || library.library_key}:${row.build_key}:${row.target_type}:${row.target_key}`
              const locked = Boolean(actionLocks[rowKey])
              return <Space size={0}>
                <Button type="link" size="small" disabled={locked || row.status === 'confirmed'} onClick={() => review(row, 'confirmed')}>确认</Button>
                <Button type="link" danger size="small" disabled={locked || row.status === 'rejected'} onClick={() => review(row, 'rejected')}>拒绝</Button>
              </Space>
            },
          },
        ]}
      />
    </>
  )
}

function AclPane({ library, onChanged }: { library: GraphLibrary; onChanged: () => void }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [permissionEnabled, setPermissionEnabled] = useState(true)
  const [userIds, setUserIds] = useState<string[]>([])
  const [roleIds, setRoleIds] = useState<string[]>([])
  const [users, setUsers] = useState<LocalUser[]>([])
  const [roles, setRoles] = useState<LocalRole[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [aclResponse, usersResponse, rolesResponse] = await Promise.all([
        adminApi.get<AclConfig>(`/admin/graph-libraries/${library.library_key}/acl`),
        adminApi.get<PageResult<LocalUser>>('/admin/users', { params: { page: 1, page_size: 100 } }),
        adminApi.get<PageResult<LocalRole>>('/admin/roles', { params: { page: 1, page_size: 100 } }),
      ])
      setPermissionEnabled(aclResponse.data.permission_enabled)
      setUserIds(aclResponse.data.entries.filter((entry) => entry.subject_type === 'user').map((entry) => entry.subject_key))
      setRoleIds(aclResponse.data.entries.filter((entry) => entry.subject_type === 'role').map((entry) => entry.subject_key))
      setUsers(usersResponse.data.items || [])
      setRoles(rolesResponse.data.items || [])
    } catch (error) {
      message.error(formatApiError(error, '权限配置加载失败'))
    } finally {
      setLoading(false)
    }
  }, [library.library_key])

  useEffect(() => {
    void load()
  }, [load])

  const save = async () => {
    setSaving(true)
    try {
      const entries = [
        ...userIds.map((subjectKey) => ({ identity_provider: 'local', subject_type: 'user', subject_key: subjectKey })),
        ...roleIds.map((subjectKey) => ({ identity_provider: 'local', subject_type: 'role', subject_key: subjectKey })),
      ]
      await adminApi.put(`/admin/graph-libraries/${library.library_key}/acl`, {
        permission_enabled: permissionEnabled,
        entries,
      })
      message.success('图谱库权限已保存')
      onChanged()
    } catch (error) {
      message.error(formatApiError(error, '权限配置保存失败'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Spin spinning={loading}>
      <div className="graph-library-acl">
        <div className="graph-library-acl-switch">
          <div><Typography.Text strong>开启权限控制</Typography.Text><Typography.Text type="secondary">关闭时所有已登录用户可使用；开启但没有授权时仅超级管理员可用。</Typography.Text></div>
          <Switch checked={permissionEnabled} onChange={setPermissionEnabled} />
        </div>
        <label>授权用户</label>
        <Select
          mode="tags"
          tokenSeparators={[',', '，', ' ']}
          value={userIds}
          disabled={!permissionEnabled}
          placeholder="选择用户或输入用户 ID"
          options={users.filter((user) => user.is_active).map((user) => ({ value: String(user.id), label: `${user.display_name || user.username} (${user.username})` }))}
          onChange={setUserIds}
        />
        <label>授权角色</label>
        <Select
          mode="tags"
          tokenSeparators={[',', '，', ' ']}
          value={roleIds}
          disabled={!permissionEnabled}
          placeholder="选择角色或输入角色 ID"
          options={roles.map((role) => ({ value: String(role.id), label: `${role.name} (${role.code})` }))}
          onChange={setRoleIds}
        />
        <div className="graph-library-pane-actions">
          <Button type="primary" icon={<LockOutlined />} loading={saving} onClick={() => void save()}>保存权限</Button>
        </div>
      </div>
    </Spin>
  )
}

function LibraryDrawer({
  libraryKey,
  onClose,
  onChanged,
}: {
  libraryKey: string | null
  onClose: () => void
  onChanged: () => void
}) {
  const [library, setLibrary] = useState<GraphLibrary | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!libraryKey) return
    setLoading(true)
    try {
      const response = await adminApi.get<GraphLibrary>(`/admin/graph-libraries/${libraryKey}`)
      setLibrary(response.data)
    } catch (error) {
      message.error(formatApiError(error, '图谱库详情加载失败'))
    } finally {
      setLoading(false)
    }
  }, [libraryKey])

  useEffect(() => {
    if (libraryKey) void load()
    else setLibrary(null)
  }, [libraryKey, load])

  const changed = useCallback(() => {
    void load()
    onChanged()
  }, [load, onChanged])

  return (
    <Drawer
      size="min(96vw, 1120px)"
      title={library ? `${library.name} · 维护` : '图谱库维护'}
      open={Boolean(libraryKey)}
      onClose={onClose}
      destroyOnHidden
    >
      {loading && !library ? <div className="graph-library-centered"><Spin /></div> : library ? (
        <Tabs
          destroyOnHidden={false}
          items={[
            { key: 'sources', label: '来源', children: <SourcesPane library={library} onChanged={changed} /> },
            { key: 'documents', label: '文档执行', children: <DocumentsPane library={library} /> },
            { key: 'builds', label: '构建任务', children: <BuildsPane library={library} onChanged={changed} /> },
            { key: 'reviews', label: '人工复核', children: <ReviewsPane library={library} /> },
            { key: 'acl', label: '权限', children: <AclPane library={library} onChanged={changed} /> },
          ]}
        />
      ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    </Drawer>
  )
}

export function GraphLibraryManageTab() {
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [status, setStatus] = useState<GraphLibraryStatus | null>(null)
  const [statusLoading, setStatusLoading] = useState(true)
  const [rows, setRows] = useState<GraphLibrary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [appliedQuery, setAppliedQuery] = useState('')
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<GraphLibrary | null>(null)
  const [drawerKey, setDrawerKey] = useState<string | null>(null)
  const [llmProfiles, setLlmProfiles] = useState<ModelProfile[]>([])
  const [embeddingProfiles, setEmbeddingProfiles] = useState<ModelProfile[]>([])

  const loadStatus = useCallback(async () => {
    setStatusLoading(true)
    try {
      const response = await adminApi.get<GraphLibraryStatus>('/admin/graph-libraries/status')
      setStatus(response.data)
    } catch (error) {
      setStatus(null)
      message.error(formatApiError(error, '图谱服务状态检查失败'))
    } finally {
      setStatusLoading(false)
    }
  }, [])

  const loadProfiles = useCallback(async () => {
    try {
      const [llm, embedding] = await Promise.all([
        adminApi.get<PageResult<ModelProfile>>('/admin/model-profiles', { params: { model_type: 'llm', page: 1, page_size: 100 } }),
        adminApi.get<PageResult<ModelProfile>>('/admin/model-profiles', { params: { model_type: 'embedding', page: 1, page_size: 100 } }),
      ])
      setLlmProfiles(llm.data.items)
      setEmbeddingProfiles(embedding.data.items)
    } catch {
      setLlmProfiles([])
      setEmbeddingProfiles([])
    }
  }, [])

  const load = useCallback(async () => {
    if (!status?.available) return
    setLoading(true)
    try {
      const response = await adminApi.get<PageResult<GraphLibrary>>('/admin/graph-libraries', {
        params: { page, page_size: pageSize, q: appliedQuery },
      })
      setRows(response.data.items)
      setTotal(response.data.total)
    } catch (error) {
      message.error(formatApiError(error, '图谱库列表加载失败'))
    } finally {
      setLoading(false)
    }
  }, [appliedQuery, page, pageSize, status?.available])

  useEffect(() => {
    void loadStatus()
    void loadProfiles()
  }, [loadProfiles, loadStatus])

  useEffect(() => {
    void load()
  }, [load])

  const saved = () => {
    setEditorOpen(false)
    setEditing(null)
    void load()
  }

  const patchLibrary = async (row: GraphLibrary, payload: Partial<GraphLibrary>) => {
    try {
      await adminApi.patch(`/admin/graph-libraries/${row.library_key}`, payload)
      message.success('状态已更新')
      await load()
    } catch (error) {
      message.error(formatApiError(error, '状态更新失败'))
    }
  }

  const removeLibrary = async (row: GraphLibrary) => {
    try {
      await adminApi.delete(`/admin/graph-libraries/${row.library_key}`)
      message.success('图谱库已移入删除状态')
      await load()
    } catch (error) {
      message.error(formatApiError(error, '图谱库删除失败'))
    }
  }

  if (statusLoading) return <div className="graph-library-centered"><Spin /></div>
  if (!status?.available) {
    const graphDatabase = status?.graph_database
    const neo4j = status?.neo4j
    return (
      <Alert
        type="warning"
        showIcon
        title={status?.enabled ? '独立图谱服务尚不可用' : '独立图谱库能力尚未启用'}
        description={
          <Space direction="vertical" size={2}>
            <span>图谱 MySQL：{graphDatabase?.available ? '可用' : graphDatabase?.configured ? '连接失败' : '未配置'}</span>
            <span>Neo4j：{neo4j?.available ? '可用' : neo4j?.configured ? '连接失败' : '未配置'}</span>
            <span>旧版图谱仍可正常使用；独立图谱 MySQL 配置完成后再创建图谱库。</span>
            <Button size="small" icon={<ReloadOutlined />} onClick={() => void loadStatus()}>重新检查</Button>
          </Space>
        }
      />
    )
  }

  return (
    <div className="graph-library-manage">
      <div className="graph-library-list-toolbar">
        <Input.Search
          allowClear
          value={query}
          placeholder="搜索图谱库名称或说明"
          onChange={(event) => setQuery(event.target.value)}
          onSearch={() => { resetPage(); setAppliedQuery(query.trim()) }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>刷新</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setEditorOpen(true) }}>新建图谱库</Button>
      </div>
      <Table<GraphLibrary>
        rowKey="library_key"
        loading={loading}
        dataSource={rows}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        scroll={{ x: 1120 }}
        columns={[
          {
            title: '图谱库',
            width: 250,
            render: (_value, row) => (
              <div className="graph-library-name-cell">
                <span className="graph-library-icon"><DatabaseOutlined /></span>
                <span><strong>{row.name}</strong><small>{row.description || '暂无说明'}</small></span>
              </div>
            ),
          },
          { title: '范围', dataIndex: 'scope', width: 90, render: (value: KnowledgeScope) => value === 'enterprise' ? '企业库' : '个人库' },
          { title: '来源范围', width: 180, ellipsis: true, render: (_value, row) => sourceSummary(row) },
          { title: '分块策略', width: 120, render: (_value, row) => `${CHUNK_STRATEGY_LABELS[row.chunk_strategy]}${row.chunk_strategy === 'fixed' ? ` ${row.chunk_size}` : ''}` },
          { title: '状态', dataIndex: 'status', width: 120, render: (value: string) => <StatusTag value={value} /> },
          { title: '文档', dataIndex: 'document_count', width: 76 },
          { title: '节点', dataIndex: 'node_count', width: 76 },
          { title: '关系', dataIndex: 'edge_count', width: 76 },
          {
            title: '权限',
            width: 100,
            render: (_value, row) => row.permission_enabled ? <Tag color="blue">按授权</Tag> : <Tag>全员</Tag>,
          },
          {
            title: '启用',
            width: 76,
            render: (_value, row) => <Switch size="small" checked={row.is_enabled} onChange={(checked) => void patchLibrary(row, { is_enabled: checked })} />,
          },
          { title: '更新时间', dataIndex: 'updated_at', width: 160, render: formatDateOrDash },
          {
            title: '操作',
            width: 210,
            fixed: 'right',
            render: (_value, row) => (
              <Space size={0}>
                <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => setDrawerKey(row.library_key)}>维护</Button>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditing(row); setEditorOpen(true) }}>编辑</Button>
                <Popconfirm title="确认软删除该图谱库？" okText="删除" cancelText="取消" onConfirm={() => void removeLibrary(row)}>
                  <Button type="link" danger size="small" icon={<DeleteOutlined />}>删除</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <LibraryEditor
        open={editorOpen}
        library={editing}
        llmProfiles={llmProfiles}
        embeddingProfiles={embeddingProfiles}
        onClose={() => { setEditorOpen(false); setEditing(null) }}
        onSaved={saved}
      />
      <LibraryDrawer libraryKey={drawerKey} onClose={() => setDrawerKey(null)} onChanged={() => void load()} />
    </div>
  )
}
